/**
 * The editable cards of a mapping recipe — the subject, the entities found in rows, the
 * facts (one relationship per pair) and the summaries (one relationship per pair and grain) —
 * plus the small controls they share. Pure; every edit goes up through onChange.
 *
 * Copy uses the platform's words — entity, relationship, property, entity type, relationship
 * type, case schema — never "nodes", "edges" or "canonicalization".
 */

import type { TFunction } from 'i18next'
import { ChevronDown, Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatCount } from '@/components/case-dashboard/dashboard-view'
import { formatPercent } from '@/components/humint/format'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  type DescriptorPreview,
  type EntityDescriptor,
  effectiveGrain,
  type Proposal,
  type RelationshipPreview,
  type RelationshipRule,
  type RowsQuery,
  type StructuredIngestTable,
  type SubjectEntity,
  withGrain,
} from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { entityLabelColor } from '@/utils/entity-colors'
import { IdentityKey } from './IdentityKey'
import { descriptorIdentity, identifyingColumns, subjectIdentity } from './identity'
import { MappingMergeToggle } from './MappingMergeToggle'
import { PhoneFormatToggle } from './PhoneFormatToggle'
import { withSubjectValue } from './recipe-edits'
import { grainChip, parseGrain } from './recipe-graph-data'
import { useSchemaIdentityProperties } from './schema-identity'
import { useTapTooltip } from './useTapTooltip'

// --- shared bits --------------------------------------------------------------------------

export function HelpTip({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const { t } = useTranslation()
  const tip = useTapTooltip()
  return (
    <Tooltip open={tip.open} onOpenChange={tip.onOpenChange}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex text-muted-foreground hover:text-foreground max-md:relative max-md:after:absolute max-md:after:-inset-2.5"
          aria-label={t('tableMapping.help')}
          {...tip.trigger}
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        className={cn(
          'text-xs leading-snug font-normal max-md:mx-2 max-md:max-w-[calc(100vw-2rem)]',
          wide ? 'max-w-sm' : 'max-w-xs'
        )}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  )
}

/** A small heading inside a card: sentence case, quiet. */
export function SectionTitle({ children, help }: { children: ReactNode; help?: ReactNode }) {
  return (
    <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      {children}
      {help && <HelpTip>{help}</HelpTip>}
    </h4>
  )
}

/** Reference material at the bottom of the review: closed until asked for. */
export function Fold({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground max-md:min-h-9 max-md:text-left">
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 transition-transform motion-reduce:transition-none',
            open && 'rotate-180'
          )}
        />
        {title}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </Collapsible>
  )
}

/** An entity or relationship type the case does not know yet: adding refuses it until approved. */
export function NewKindControl({
  matches,
  accepted,
  additions,
  onAccept,
}: {
  matches: boolean | undefined
  accepted: boolean | undefined
  additions?: string[]
  onAccept: (accepted: boolean) => void
}) {
  const { t } = useTranslation()
  if (matches !== false) return null
  return (
    <label className="flex flex-wrap items-center gap-1.5 text-xs">
      <Badge variant="warning">{t('tableMapping.card.schemaAddition')}</Badge>
      <Checkbox checked={!!accepted} onCheckedChange={v => onAccept(v === true)} />
      {t('tableMapping.card.approveAddition')}
      <span className="basis-full font-mono text-muted-foreground">{additions?.join('; ')}</span>
      <HelpTip>{t('tableMapping.card.approveAdditionHelp')}</HelpTip>
    </label>
  )
}

export function NullTokensInput({
  value,
  onChange,
}: {
  value: string[]
  onChange: (tokens: string[]) => void
}) {
  const { t } = useTranslation()
  const [text, setText] = useState(value.join(', '))
  useEffect(() => setText(value.join(', ')), [value])
  return (
    <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {t('tableMapping.columns.nullTokens')}
      <HelpTip>{t('tableMapping.columns.nullTokensHelp')}</HelpTip>
      <Input
        className="md:h-7 md:w-56"
        placeholder={t('tableMapping.columns.nullTokensPlaceholder')}
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={() =>
          onChange(
            text
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          )
        }
      />
    </label>
  )
}

export function SchemaTypeSelect({
  value,
  labels,
  onChange,
}: {
  value: string
  labels: string[]
  onChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const options = Array.from(new Set([value, ...labels].filter(Boolean)))
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-44 text-xs" aria-label={t('tableMapping.card.entityType')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {o.replace(/^new:/, '')}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function KindDot({ label }: { label: string }) {
  return (
    <span
      className={cn('inline-block size-2.5 shrink-0 rounded-full', entityLabelColor(label))}
      aria-hidden
    />
  )
}

/** Below `md` a field's label (and its help) sits above its full-width input. */
const FIELD_LABEL =
  'flex items-center gap-1.5 text-xs text-muted-foreground max-md:w-full max-md:flex-wrap'

function cardClass(enabled: boolean) {
  return cn('flex flex-col gap-3 rounded-lg border p-3', !enabled && 'opacity-60')
}

/** The on/off checkbox and name every card starts with. */
function CardToggle({
  enabled,
  label,
  children,
  onChange,
}: {
  enabled: boolean
  label?: string
  children: ReactNode
  onChange: (enabled: boolean) => void
}) {
  return (
    <label className="flex min-w-0 items-center gap-2 text-sm font-medium">
      <Checkbox checked={enabled} onCheckedChange={v => onChange(v === true)} />
      {label && <KindDot label={label} />}
      <span className="min-w-0 truncate">{children}</span>
    </label>
  )
}

function IdentityPropertySelect({
  id,
  identity,
  onChange,
}: {
  id: string
  identity: SubjectEntity | EntityDescriptor
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const schemaProperties = useSchemaIdentityProperties()
  const value = identity.unique_property?.trim() || ''
  const properties = schemaProperties?.[identity.schema_type.replace(/^new:/, '')]
  const options = [
    ...new Set([...Object.keys(properties ?? {}), ...(identity.identity_properties ?? [])]),
  ]
  const invalidChoice = value && !options.includes(value)
  const lacksUniqueKey = !!value && !identity.identity_properties?.includes(value)
  const invalid = !!identity.identity_error || (identity.merge_enabled !== false && lacksUniqueKey)
  return (
    <Select
      value={value || '__none__'}
      disabled={!properties && !identity.identity_properties}
      onValueChange={next => onChange(next === '__none__' ? '' : next)}
    >
      <SelectTrigger
        id={id}
        className="order-last font-mono md:order-none md:h-7 md:w-44"
        aria-invalid={invalid}
      >
        <SelectValue placeholder={t('tableMapping.card.storedInPlaceholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">{t('tableMapping.identity.noProperty')}</SelectItem>
        {invalidChoice && (
          <SelectItem value={value} disabled>
            {value}
          </SelectItem>
        )}
        {options.map(property => (
          <SelectItem key={property} value={property}>
            {property}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** The property used for identity, validated against the case schema. */
function PropertyField({
  identity,
  onChange,
}: {
  identity: SubjectEntity | EntityDescriptor
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className={FIELD_LABEL}>
      <label htmlFor={id}>
        {t(
          identity.merge_enabled === false
            ? 'tableMapping.card.mappedProperty'
            : 'tableMapping.card.storedIn'
        )}
      </label>
      <IdentityPropertySelect id={id} identity={identity} onChange={onChange} />
      <HelpTip>{t('tableMapping.card.storedInHelp')}</HelpTip>
    </div>
  )
}

function MappingIdentityControls<T extends SubjectEntity | EntityDescriptor>({
  item,
  onChange,
}: {
  item: T
  onChange: (next: T) => void
}) {
  const { t } = useTranslation()
  if (item.identity_rule?.trim().toLowerCase() === 'source_row' && item.merge_enabled !== false)
    return null
  return (
    <div className="flex flex-col gap-2">
      <PropertyField
        identity={item}
        onChange={value =>
          onChange({ ...item, unique_property: value, schema_match: false, accept_new_type: false })
        }
      />
      <MappingMergeToggle item={item} onChange={onChange} />
      {item.schema_type === 'PhoneNumber' && <PhoneFormatToggle item={item} onChange={onChange} />}
      {'identity_columns' in item && (
        <p className="text-xs text-muted-foreground">{t('tableMapping.card.changeIdentifier')}</p>
      )}
    </div>
  )
}

// --- entities ------------------------------------------------------------------------------

export function SubjectEntityCard({
  entity,
  schemaLabels,
  onChange,
}: {
  entity: SubjectEntity
  schemaLabels: string[]
  onChange: (next: SubjectEntity) => void
}) {
  const { t } = useTranslation()
  const enabled = entity.enabled ?? true
  const valueId = useId()
  return (
    <div className={cardClass(enabled)}>
      <div className="flex flex-wrap items-center gap-3">
        <CardToggle
          enabled={enabled}
          label={entity.schema_type}
          onChange={v => onChange({ ...entity, enabled: v })}
        >
          {entity.name}
        </CardToggle>
        <SchemaTypeSelect
          value={entity.schema_type}
          labels={schemaLabels}
          onChange={v =>
            onChange({
              ...entity,
              schema_type: v,
              schema_match: false,
              accept_new_type: false,
              identity_properties: undefined,
              identity_error: null,
            })
          }
        />
        <NewKindControl
          matches={entity.schema_match}
          accepted={entity.accept_new_type}
          additions={entity.schema_additions}
          onAccept={v => onChange({ ...entity, accept_new_type: v })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <SectionTitle help={t('tableMapping.identity.constantHelp')}>
          {t('tableMapping.card.identifier')}
        </SectionTitle>
        <IdentityKey identity={subjectIdentity(entity)} />
        <div className="flex flex-wrap items-center gap-3">
          <div className={FIELD_LABEL}>
            <label htmlFor={valueId}>{t('tableMapping.card.fileValue')}</label>
            <Input
              id={valueId}
              className="order-last md:order-none md:h-7 md:w-56"
              value={entity.identity_value ?? ''}
              onChange={e => onChange(withSubjectValue(entity, e.target.value))}
            />
            <HelpTip>{t('tableMapping.card.fileValueHelp')}</HelpTip>
          </div>
          <MappingIdentityControls item={entity} onChange={onChange} />
        </div>
      </div>
      {entity.note && <p className="text-xs text-muted-foreground">{entity.note}</p>}
    </div>
  )
}

function DescriptorCount({
  preview,
  rowsTotal,
}: {
  preview: DescriptorPreview
  rowsTotal: number
}) {
  const { t, i18n } = useTranslation()
  return (
    <p className="text-sm">
      <span className="text-xl font-semibold tabular-nums">
        {formatCount(preview.canonical_distinct, i18n.language)}
      </span>{' '}
      <span className="text-muted-foreground">
        {t('tableMapping.card.distinct', {
          count: preview.canonical_distinct,
          rows: preview.rows_with_identity,
          total: rowsTotal,
        })}
        <Spellings stats={preview} />
      </span>
    </p>
  )
}

/** ", merged from 39 spellings" with what is unified first; nothing when none were merged. */
export function Spellings({ stats }: { stats: DescriptorPreview }) {
  const { t } = useTranslation()
  if (stats.raw_distinct === stats.canonical_distinct) return null
  return (
    <>
      {', '}
      <span className="inline-flex items-center gap-1">
        {t('tableMapping.outline.spellings', { count: stats.raw_distinct })}
        <HelpTip>{t('tableMapping.outline.spellingsHelp')}</HelpTip>
      </span>
    </>
  )
}

export function DescriptorCard({
  descriptor,
  preview,
  rowsTotal,
  schemaLabels,
  onChange,
}: {
  descriptor: EntityDescriptor
  preview: DescriptorPreview | null
  rowsTotal: number
  schemaLabels: string[]
  onChange: (next: EntityDescriptor) => void
}) {
  const { t } = useTranslation()
  const enabled = descriptor.enabled ?? true
  const identity = descriptorIdentity(descriptor, preview)
  return (
    <div className={cardClass(enabled)}>
      <div className="flex flex-wrap items-center gap-3">
        <CardToggle
          enabled={enabled}
          label={descriptor.schema_type}
          onChange={v => onChange({ ...descriptor, enabled: v })}
        >
          {descriptor.name}
        </CardToggle>
        <SchemaTypeSelect
          value={descriptor.schema_type}
          labels={schemaLabels}
          onChange={v =>
            onChange({
              ...descriptor,
              schema_type: v,
              schema_match: false,
              accept_new_type: false,
              identity_properties: undefined,
              identity_error: null,
            })
          }
        />
        <NewKindControl
          matches={descriptor.schema_match}
          accepted={descriptor.accept_new_type}
          additions={descriptor.schema_additions}
          onAccept={v => onChange({ ...descriptor, accept_new_type: v })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <SectionTitle help={t('tableMapping.card.identifierHelp')}>
          {t('tableMapping.card.identifier')}
        </SectionTitle>
        <IdentityKey identity={identity} />
        <MappingIdentityControls item={descriptor} onChange={onChange} />
      </div>
      {(descriptor.filters?.length ?? 0) > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('tableMapping.card.onlyRows', {
            conditions: descriptor.filters?.map(filter => `${filter.column}: ${filter.keep}`),
          })}
        </p>
      )}
      {descriptor.note && <p className="text-xs text-muted-foreground">{descriptor.note}</p>}
      {preview && enabled && <DescriptorCount preview={preview} rowsTotal={rowsTotal} />}
    </div>
  )
}

// --- relationships ------------------------------------------------------------------------

function typeToken(type: string): string {
  const bare = type.replace(/^new:/, '').trim()
  const parts = bare.split(/\s+/)
  return parts.length === 3 ? parts[1] : bare
}

function LinkHeader({
  rule,
  onChange,
}: {
  rule: RelationshipRule
  onChange: (next: RelationshipRule) => void
}) {
  const { t } = useTranslation()
  const typeId = useId()
  const enabled = rule.enabled ?? true
  return (
    <div className="flex flex-wrap items-center gap-3">
      <CardToggle enabled={enabled} onChange={v => onChange({ ...rule, enabled: v })}>
        {rule.from} → {rule.to}
      </CardToggle>
      <div className={FIELD_LABEL}>
        <label htmlFor={typeId}>{t('tableMapping.card.relationshipType')}</label>
        <Input
          id={typeId}
          className="font-mono md:h-7 md:w-44"
          value={typeToken(rule.type)}
          onChange={e =>
            onChange({ ...rule, type: e.target.value, schema_match: false, accept_new_type: false })
          }
        />
      </div>
      <NewKindControl
        matches={rule.schema_match}
        accepted={rule.accept_new_type}
        additions={rule.schema_additions}
        onAccept={v => onChange({ ...rule, accept_new_type: v })}
      />
      <Select
        value={rule.kind ?? 'event_aggregate'}
        onValueChange={v => onChange({ ...rule, kind: v as RelationshipRule['kind'] })}
      >
        <SelectTrigger className="h-8 w-60 text-xs" aria-label={t('tableMapping.card.kindLabel')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="event_aggregate">{t('tableMapping.card.kindAggregate')}</SelectItem>
          <SelectItem value="structural">{t('tableMapping.card.kindFact')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

export function FactCard({
  rule,
  preview,
  onChange,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview | null
  onChange: (next: RelationshipRule) => void
}) {
  const { t } = useTranslation()
  const enabled = rule.enabled ?? true
  const count = preview?.grains.pair
  return (
    <div className={cardClass(enabled)}>
      <LinkHeader rule={rule} onChange={onChange} />
      <p className="text-xs text-muted-foreground">
        {count !== undefined
          ? t('tableMapping.card.factCount', { count })
          : t('tableMapping.grain.fact')}
        {rule.note && `. ${rule.note}`}
      </p>
    </div>
  )
}

/** A synthetic column name means the table had no header row — say which column it is. */
function columnLabel(column: string, t: TFunction): string {
  const synthetic = /^col_(\d+)$/.exec(column)
  return synthetic
    ? t('tableMapping.card.syntheticColumn', { number: synthetic[1] })
    : `“${column}”`
}

function grainMeaning(grain: string, rule: RelationshipRule, t: TFunction): string {
  const parsed = parseGrain(grain)
  if (parsed.kind === 'pair') {
    return t('tableMapping.card.grainMeaning.pair', { from: rule.from, to: rule.to })
  }
  if (parsed.kind === 'category') {
    return t('tableMapping.card.grainMeaning.category', {
      column: columnLabel(parsed.column, t),
      to: rule.to,
    })
  }
  if (parsed.kind === 'other')
    return t('tableMapping.card.grainMeaning.other', { grain: parsed.grain })
  return t(`tableMapping.card.grainMeaning.${parsed.kind}`)
}

function GrainHelp({ grains, rule }: { grains: string[]; rule: RelationshipRule }) {
  const { t } = useTranslation()
  return (
    <HelpTip wide>
      <div className="flex flex-col gap-1.5">
        <p>{t('tableMapping.card.grainIntro')}</p>
        <ul className="flex flex-col gap-1 pl-3">
          {grains.map(g => (
            <li key={g} className="list-disc">
              <span className="font-semibold">{grainChip(g, t)}</span>: {grainMeaning(g, rule, t)}
            </li>
          ))}
        </ul>
        <p>{t('tableMapping.card.grainCount')}</p>
      </div>
    </HelpTip>
  )
}

/** When every chip lands on the same number the choice changes nothing — say so. */
function sameCountNote(preview: RelationshipPreview, t: TFunction): string | null {
  const counts = Object.values(preview.grains)
  if (counts.length < 2 || new Set(counts).size !== 1) return null
  return preview.grains.pair === preview.rows_in_scope
    ? t('tableMapping.card.sameCountSingleRows', { count: counts[0] })
    : t('tableMapping.card.sameCount', { count: counts[0] })
}

function GrainChips({
  rule,
  preview,
  onChange,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview | null
  onChange: (next: RelationshipRule) => void
}) {
  const { t, i18n } = useTranslation()
  const grain = effectiveGrain(rule, preview)
  const grains = preview ? Object.keys(preview.grains) : ['pair']
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{t('tableMapping.card.onePer')}</span>
      {grains.map(g => {
        const n = preview?.grains[g]
        const active = g === grain
        return (
          <button
            key={g}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(withGrain(rule, g))}
            className={cn(
              'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors md:h-7 md:px-2.5',
              active
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            )}
          >
            {grainChip(g, t)}
            {n !== undefined && (
              <span className={cn('tabular-nums', active ? 'font-medium' : '')}>
                {formatCount(n, i18n.language)}
              </span>
            )}
          </button>
        )
      })}
      <GrainHelp grains={grains} rule={rule} />
    </div>
  )
}

function TopPairs({
  rule,
  preview,
  onShowRows,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview
  onShowRows: (query: RowsQuery, subtitle: string) => void
}) {
  const { t } = useTranslation()
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground max-md:py-1.5">
        {t('tableMapping.card.topPairs')}
      </summary>
      <Table className="max-md:min-w-[22rem] max-md:table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead>{t('tableMapping.card.from')}</TableHead>
            <TableHead>{t('tableMapping.card.to')}</TableHead>
            <TableHead className="text-right max-md:w-12">{t('tableMapping.card.rows')}</TableHead>
            <TableHead className="text-right max-md:w-24 max-md:whitespace-normal">
              {preview.measure
                ? t('tableMapping.card.totalOf', { column: preview.measure })
                : t('tableMapping.card.total')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {preview.top.map(row => (
            <TableRow
              key={`${row.from ?? ''}|${row.to}`}
              className="cursor-pointer"
              onClick={() =>
                onShowRows(
                  { relationship: rule.name, fromKey: row.from ?? null, toKey: row.to },
                  `${row.from ? `${row.from} → ` : ''}${row.to}`
                )
              }
            >
              <TableCell className="font-mono max-md:whitespace-normal max-md:break-all">
                {row.from ?? ''}
              </TableCell>
              <TableCell className="font-mono max-md:whitespace-normal max-md:break-all">
                {row.to}
              </TableCell>
              <TableCell className="text-right tabular-nums">{row.rows}</TableCell>
              <TableCell className="text-right tabular-nums">{row.sum ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </details>
  )
}

function SummaryFacts({
  rule,
  preview,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview | null
}) {
  const { t } = useTranslation()
  const skipped = preview ? preview.rows_total - preview.rows_in_scope : 0
  const parts = [
    preview
      ? t('tableMapping.card.builtFrom', {
          inScope: preview.rows_in_scope,
          total: preview.rows_total,
        })
      : t('tableMapping.card.countsAfter'),
    skipped > 0 && t('tableMapping.card.skipped', { count: skipped }),
    rule.direction_rule && t('tableMapping.card.directionRule', { rule: rule.direction_rule }),
    rule.note,
  ].filter(Boolean)
  return <p className="text-xs text-muted-foreground">{parts.join('. ')}</p>
}

export function SummaryCard({
  rule,
  preview,
  measureColumns,
  onShowRows,
  onChange,
}: {
  rule: RelationshipRule
  preview: RelationshipPreview | null
  measureColumns: string[]
  onShowRows: (query: RowsQuery, subtitle: string) => void
  onChange: (next: RelationshipRule) => void
}) {
  const { t, i18n } = useTranslation()
  const enabled = rule.enabled ?? true
  const grain = effectiveGrain(rule, preview)
  const measures = Array.from(new Set([...(rule.measures ?? []), ...measureColumns]))
  const count = preview?.grains[grain]
  const directionUnavailable =
    !!preview && !!rule.direction_rule && !('direction' in preview.grains)
  const same = preview ? sameCountNote(preview, t) : null
  return (
    <div className={cardClass(enabled)}>
      <LinkHeader rule={rule} onChange={onChange} />
      <SummaryFacts rule={rule} preview={preview} />
      <GrainChips rule={rule} preview={preview} onChange={onChange} />
      {same && <p className="text-xs text-muted-foreground">{same}</p>}
      {directionUnavailable && (
        <p className="text-xs text-warning-foreground">
          {t('tableMapping.card.directionUnavailable')}
        </p>
      )}
      {count !== undefined && (
        <p className="text-sm">
          <span className="text-xl font-semibold tabular-nums">
            {formatCount(count, i18n.language)}
          </span>{' '}
          <span className="text-muted-foreground">
            {t('tableMapping.card.willCreate', { count })}
          </span>
        </p>
      )}
      {measures.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {measures.map(m => (
            <label key={m} className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={(rule.measures ?? []).includes(m)}
                onCheckedChange={v =>
                  onChange({
                    ...rule,
                    measures:
                      v === true
                        ? [...(rule.measures ?? []), m]
                        : (rule.measures ?? []).filter(x => x !== m),
                  })
                }
              />
              {t('tableMapping.card.totalOf', { column: m })}
            </label>
          ))}
        </div>
      )}
      {preview && preview.top.length > 0 && (
        <TopPairs rule={rule} preview={preview} onShowRows={onShowRows} />
      )}
    </div>
  )
}

// --- columns -------------------------------------------------------------------------------

function HeadWithHelp({ children, help }: { children: ReactNode; help: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <HelpTip>{help}</HelpTip>
    </span>
  )
}

export function ColumnsTable({
  table,
  recipe,
}: {
  table: StructuredIngestTable
  recipe: Proposal
}) {
  const { t, i18n } = useTranslation()
  const roles = new Map((recipe.columns ?? []).map(c => [c.name, c]))
  // A column identifies an entity when that entity's identifier is built from it — the
  // annotation's role alone can say otherwise.
  const identifies = identifyingColumns(recipe.entity_descriptors ?? [])
  const roleWord = (role: string) => t(`tableMapping.columns.roles.${role}`, { defaultValue: role })
  return (
    <div className="max-h-96 overflow-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('tableMapping.columns.column')}</TableHead>
            <TableHead>
              <HeadWithHelp help={t('tableMapping.columns.usedAsHelp')}>
                {t('tableMapping.columns.usedAs')}
              </HeadWithHelp>
            </TableHead>
            <TableHead>{t('tableMapping.columns.belongsTo')}</TableHead>
            <TableHead className="text-right">
              <HeadWithHelp help={t('tableMapping.columns.distinctHelp')}>
                {t('tableMapping.columns.distinct')}
              </HeadWithHelp>
            </TableHead>
            <TableHead>
              <HeadWithHelp help={t('tableMapping.columns.looksLikeHelp')}>
                {t('tableMapping.columns.looksLike')}
              </HeadWithHelp>
            </TableHead>
            <TableHead>{t('tableMapping.columns.examples')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {table.columns.map(c => {
            const r = roles.get(c.name)
            const owners = identifies.get(c.name)
            // a sparse marker is described by what it is, whatever role the annotator filed it under
            const annotated = c.kind === 'tag' || c.kind === 'empty' ? c.kind : (r?.role ?? c.kind)
            const role = owners
              ? 'identity'
              : annotated === 'identity'
                ? (c.kind ?? 'string')
                : annotated
            return (
              <TableRow key={c.name}>
                <TableCell className="font-mono text-xs">{c.name}</TableCell>
                <TableCell>
                  <Badge variant={owners ? 'default' : 'secondary'}>{roleWord(role)}</Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {owners ? owners.join(', ') : (r?.entity ?? '')}
                  {r?.alias && (
                    <span className="text-muted-foreground">
                      {owners || r?.entity ? ', ' : ''}
                      {t('tableMapping.columns.storedAs')}{' '}
                      <span className="font-mono text-foreground">{r.alias}</span>
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {formatCount(c.n_distinct, i18n.language)}
                  {c.filled_rows !== undefined && c.null_rate > 0.05 && (
                    <span className="text-muted-foreground">
                      {' '}
                      {t('tableMapping.columns.inRows', { count: c.filled_rows })}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  {Object.entries(c.recognizers)
                    .map(
                      ([k, v]) =>
                        `${t(`tableMapping.columns.recognizers.${k}`, { defaultValue: k })} ${formatPercent(v, i18n.language)}`
                    )
                    .join(', ')}
                </TableCell>
                <TableCell className="max-w-48 truncate font-mono text-xs">
                  {c.samples.slice(0, 2).join(', ')}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
